/**
 * Model registry — single source of truth for actor-identity model badges.
 *
 * Resolves a `modelRef` (the string stored on actor / template / activation rows)
 * into everything the UI needs to render a per-model bordered pill: family,
 * compact display label, provider key for the icon, and routing flags.
 *
 * Adding a new model = one entry in MODEL_FAMILIES (the keyword sniff) + one
 * entry in FAMILY_ACCENTS (the palette).
 */

export type ModelFamily =
  | "fable"
  | "opus"
  | "sonnet"
  | "haiku"
  | "gpt"
  | "gpt-codex"
  | "gemini-pro"
  | "gemini-flash"
  | "deepseek"
  | "kimi"
  | "glm"
  | "grok"
  | "minimax"
  | "nova"
  | "unknown";

export type ModelProvider =
  | "anthropic"
  | "azure"
  | "deepseek"
  | "openai"
  | "google"
  | "zhipu"
  | "minimax"
  | "openrouter"
  | "xai"
  | "xiaomi"
  | "unknown";

export interface FamilyAccent {
  /** Border + ring color, e.g. "border-violet-400/30". */
  border: string;
  /** Text color, e.g. "text-violet-300". */
  text: string;
  /** Slightly muted text used when isBedrock=true (whispers "via Bedrock"). */
  textBedrock: string;
}

export interface ResolvedModel {
  family: ModelFamily;
  /** Compact display label, e.g. "OPUS 4.7", "GPT 5.4 CODEX", "MiniMax M3". */
  label: string;
  provider: ModelProvider;
  /** True when modelRef goes through AWS Bedrock (vs Anthropic-direct, Vertex, etc). */
  isBedrock: boolean;
}

const FAMILY_ACCENTS: Record<ModelFamily, FamilyAccent> = {
  // DeepSeek — sky/cyan family, distinct from MiniMax amber and OpenAI emerald
  deepseek: {
    border: "border-sky-500/40 dark:border-sky-400/30",
    text: "text-sky-700 dark:text-sky-300",
    textBedrock: "text-sky-700/80 dark:text-sky-400",
  },
  // Anthropic family — cool blue/violet hue progression by tier
  fable: {
    border: "border-purple-500/40 dark:border-purple-400/30",
    text: "text-purple-700 dark:text-purple-300",
    textBedrock: "text-purple-700/80 dark:text-purple-400",
  },
  "gemini-flash": {
    border: "border-pink-500/40 dark:border-pink-400/30",
    text: "text-pink-700 dark:text-pink-300",
    textBedrock: "text-pink-700/80 dark:text-pink-400",
  },
  // Google — magenta family (avoid stealing violet from opus)
  "gemini-pro": {
    border: "border-fuchsia-500/40 dark:border-fuchsia-400/30",
    text: "text-fuchsia-700 dark:text-fuchsia-300",
    textBedrock: "text-fuchsia-700/80 dark:text-fuchsia-400",
  },
  glm: {
    border: "border-blue-500/40 dark:border-blue-400/30",
    text: "text-blue-700 dark:text-blue-300",
    textBedrock: "text-blue-700/80 dark:text-blue-400",
  },
  // OpenAI — emerald (matches existing OpenAI ProviderIcon color)
  gpt: {
    border: "border-emerald-500/40 dark:border-emerald-400/30",
    text: "text-emerald-700 dark:text-emerald-300",
    textBedrock: "text-emerald-700/80 dark:text-emerald-400",
  },
  // Gpt-codex — teal (in-family with gpt, distinct read)
  "gpt-codex": {
    border: "border-teal-500/40 dark:border-teal-400/30",
    text: "text-teal-700 dark:text-teal-300",
    textBedrock: "text-teal-700/80 dark:text-teal-400",
  },
  // XAI — neutral slate (matches existing xAI ProviderIcon color)
  grok: {
    border: "border-slate-500/40 dark:border-slate-400/30",
    text: "text-slate-700 dark:text-slate-300",
    textBedrock: "text-slate-600 dark:text-slate-400",
  },
  haiku: {
    border: "border-cyan-500/40 dark:border-cyan-400/30",
    text: "text-cyan-700 dark:text-cyan-300",
    textBedrock: "text-cyan-700/80 dark:text-cyan-400",
  },
  kimi: {
    border: "border-zinc-500/40 dark:border-zinc-300/30",
    text: "text-zinc-700 dark:text-zinc-200",
    textBedrock: "text-zinc-700/80 dark:text-zinc-300",
  },
  // MiniMax — amber, distinct from OpenAI emerald with its own provider icon
  minimax: {
    border: "border-amber-500/40 dark:border-amber-400/30",
    text: "text-amber-700 dark:text-amber-300",
    textBedrock: "text-amber-700/80 dark:text-amber-400",
  },
  // Amazon — orange (Bedrock-native model, AWS warmth)
  nova: {
    border: "border-orange-500/40 dark:border-orange-400/30",
    text: "text-orange-700 dark:text-orange-300",
    textBedrock: "text-orange-700/80 dark:text-orange-400",
  },
  opus: {
    border: "border-violet-500/40 dark:border-violet-400/30",
    text: "text-violet-700 dark:text-violet-300",
    textBedrock: "text-violet-700/80 dark:text-violet-400",
  },
  sonnet: {
    border: "border-indigo-500/40 dark:border-indigo-400/30",
    text: "text-indigo-700 dark:text-indigo-300",
    textBedrock: "text-indigo-700/80 dark:text-indigo-400",
  },
  unknown: {
    border: "border-slate-400 dark:border-slate-700",
    text: "text-slate-600 dark:text-slate-400",
    textBedrock: "text-slate-500 dark:text-slate-500",
  },
};

/** Look up the palette for a family. */
export function getFamilyAccent(family: ModelFamily): FamilyAccent {
  return FAMILY_ACCENTS[family];
}

/**
 * Strip routing prefix (anthropic/, bedrock/, bedrock-openai/, vertex/, vertex/xai/, etc.) and
 * dotted Bedrock paths (`global.anthropic.claude-...`) so we keep just the
 * leaf model id. Date suffix (`-20251001-v1:0`) is also dropped.
 */
const DATE_SUFFIX_RE = /-\d{8}(?:-v\d+(?::\d+)?)?$/i;

function stripToLeafModel(modelRef: string): string {
  const parts = modelRef.split("/");
  let tail: string;
  if (
    parts.length >= 3 &&
    (parts[0] === "vertex" || parts[0] === "openrouter")
  ) {
    // Vertex/xai/grok-... → grok-...    openrouter/minimax/minimax-... → minimax-...
    tail = parts.slice(2).join("/");
  } else if (parts.length >= 2) {
    // Anthropic/claude-... → claude-...    bedrock/global.anthropic.claude-... → claude-...
    // bedrock-openai/openai.gpt-5.5 → gpt-5.5
    const afterPrefix = parts.slice(1).join("/");
    // The dotted leaf format ("global.anthropic.claude-...") is Bedrock-only.
    // Splitting on `.` for OpenAI refs would mangle their version strings —
    // `openai/gpt-5.5` → `["gpt-5", "5"]` → `"5"` → unknown family. Gate the
    // split on the bedrock prefix.
    if (parts[0] === "bedrock-openai") {
      tail = afterPrefix.replace(/^openai\./i, "");
    } else if (parts[0] === "bedrock-anthropic") {
      tail = afterPrefix.replace(/^anthropic\./i, "");
    } else if (parts[0] === "bedrock") {
      const dotParts = afterPrefix.split(".");
      tail = dotParts[dotParts.length - 1] ?? afterPrefix;
    } else {
      tail = afterPrefix;
    }
  } else {
    tail = modelRef;
  }
  return tail.replace(DATE_SUFFIX_RE, "");
}

function resolveProvider(modelRef: string): ModelProvider {
  const parts = modelRef.split("/");
  // Vertex routes to its sub-provider (vertex/xai/..., vertex/google/...)
  if (parts.length >= 3 && parts[0] === "vertex") {
    const sub = parts[1].toLowerCase();
    if (sub === "xai") {
      return "xai";
    }
    if (sub === "google") {
      return "google";
    }
  }
  if (parts.length >= 3 && parts[0] === "openrouter") {
    return resolveOpenRouterProvider(parts[1]);
  }
  if (parts.length >= 2) {
    const head = parts[0].toLowerCase();
    if (
      head === "anthropic" ||
      head === "bedrock" ||
      head === "bedrock-anthropic"
    ) {
      return "anthropic";
    }
    if (head === "openai" || head === "bedrock-openai") {
      return "openai";
    }
    if (head === "azure") {
      return "azure";
    }
    if (head === "deepseek" || head === "minimax" || head === "xiaomi") {
      return head;
    }
    if (head === "z-ai" || head === "glm") {
      return "zhipu";
    }
    if (head === "google" || head === "vertex") {
      return "google";
    }
  }
  // Bare modelId — keyword sniff so unprefixed refs still work
  const lower = modelRef.toLowerCase();
  if (lower.includes("claude") || lower.includes("anthropic")) {
    return "anthropic";
  }
  if (
    lower.includes("gpt") ||
    lower.startsWith("o1") ||
    lower.startsWith("o3")
  ) {
    return "openai";
  }
  if (lower.includes("gemini") || lower.includes("gemma")) {
    return "google";
  }
  if (lower.includes("grok")) {
    return "xai";
  }
  if (lower.includes("glm") || lower.includes("z-ai")) {
    return "zhipu";
  }
  if (lower.includes("nova")) {
    return "anthropic";
  } // Nova is Bedrock-only; provider icon falls back to AWS via anthropic key
  return "unknown";
}

function resolveOpenRouterProvider(value: string | undefined): ModelProvider {
  const provider = value?.toLowerCase();
  if (provider === "z-ai" || provider === "glm") {
    return "zhipu";
  }
  if (
    provider === "deepseek" ||
    provider === "minimax" ||
    provider === "x-ai" ||
    provider === "xai" ||
    provider === "xiaomi"
  ) {
    if (provider === "x-ai" || provider === "xai") {
      return "xai";
    }
    return provider;
  }
  return "openrouter";
}

/**
 * Detect family + extract version from the leaf model id.
 * Examples: claude-opus-4-7 → opus 4.7    gpt-5.4-codex → gpt-codex 5.4
 *           gemini-3.1-pro-preview → gemini-pro 3.1    grok-4.1-fast-reasoning → grok 4.1 FAST
 */
function detectFamilyAndLabel(leaf: string): {
  family: ModelFamily;
  label: string;
} {
  const lower = leaf.toLowerCase();

  const claude = detectClaudeFamilyAndLabel(lower);
  if (claude) {
    return claude;
  }

  const gpt = detectGptFamilyAndLabel(lower);
  if (gpt) {
    return gpt;
  }

  // Gemini — gemini-3.1-pro-preview / gemini-3-flash-preview / gemini-2.5-flash-lite
  const geminiMatch = /^gemini-(\d+(?:\.\d+)?)-?(pro|flash)?(?:-(lite))?/.exec(
    lower,
  );
  if (geminiMatch) {
    const [, ver, tierMatch, lite] = geminiMatch;
    // Bare gemini-X defaults to flash
    const tier = tierMatch ?? "flash";
    const family: ModelFamily = tier === "pro" ? "gemini-pro" : "gemini-flash";
    return {
      family,
      label: lite
        ? `GEMINI ${ver} ${tier.toUpperCase()} LITE`
        : `GEMINI ${ver} ${tier.toUpperCase()}`,
    };
  }

  // DeepSeek — deepseek-v4-pro, deepseek-v5, deepseek-v5-x
  const deepseekMatch = /^deepseek-v(\d+(?:\.\d+)?)(?:-([a-z0-9]+))?/.exec(
    lower,
  );
  if (deepseekMatch) {
    const [, ver, tier] = deepseekMatch;
    return {
      family: "deepseek",
      label: tier
        ? `DEEPSEEK V${ver} ${tier.toUpperCase()}`
        : `DEEPSEEK V${ver}`,
    };
  }

  const kimiMatch = /^kimi-k(\d+(?:\.\d+)?)/.exec(lower);
  if (kimiMatch) {
    const [, ver] = kimiMatch;
    return { family: "kimi", label: `Kimi K${ver}` };
  }

  const glmMatch = /^glm-(\d+(?:\.\d+)?)(?::(fast)|-(fast))?/.exec(lower);
  if (glmMatch) {
    const [, ver, taggedFast, legacyFast] = glmMatch;
    return {
      family: "glm",
      label:
        taggedFast === "fast" || legacyFast === "fast"
          ? `GLM ${ver} FAST`
          : `GLM ${ver}`,
    };
  }

  const mimoMatch = /^mimo-v(\d+(?:\.\d+)?)(?:-([a-z0-9]+))?/.exec(lower);
  if (mimoMatch) {
    const [, ver, tier] = mimoMatch;
    return {
      family: "unknown",
      label: tier ? `MIMO V${ver} ${tier.toUpperCase()}` : `MIMO V${ver}`,
    };
  }

  // Grok — grok-4.1-fast-reasoning / grok-4.1-fast-non-reasoning / grok-4.20-non-reasoning
  const grokMatch =
    /^grok-(\d+(?:\.\d+)?)(?:-(fast))?(?:-(non-reasoning|reasoning))?/.exec(
      lower,
    );
  if (grokMatch) {
    const [, ver, fastFlag, reasoning] = grokMatch;
    const parts = [`GROK ${ver}`];
    if (fastFlag === "fast") {
      parts.push("FAST");
    }
    if (reasoning === "reasoning") {
      parts.push("RZN");
    }
    // Non-reasoning is the implicit default — leave it off to keep the chip compact
    return { family: "grok", label: parts.join(" ") };
  }

  // MiniMax — minimax-m2.7 / minimax-m3
  const minimaxMatch = /^minimax-m(\d+(?:\.\d+)?)/.exec(lower);
  if (minimaxMatch) {
    const [, ver] = minimaxMatch;
    return { family: "minimax", label: `MiniMax M${ver}` };
  }

  // Nova — amazon.nova-2-lite-v1:0
  const novaMatch = /nova-(\d+)(?:-(lite|pro|micro))?/.exec(lower);
  if (novaMatch) {
    const [, ver, tier] = novaMatch;
    return {
      family: "nova",
      label: tier ? `NOVA ${ver} ${tier.toUpperCase()}` : `NOVA ${ver}`,
    };
  }

  return { family: "unknown", label: leaf.toUpperCase() };
}

function familyForClaudeTier(tier: string): "opus" | "sonnet" | "haiku" {
  if (tier === "opus") {
    return "opus";
  }
  if (tier === "sonnet") {
    return "sonnet";
  }
  return "haiku";
}

/** Resolve a modelRef into everything the badge needs. */
export function resolveModel(modelRef: string): ResolvedModel {
  const isBedrock =
    modelRef.startsWith("bedrock/") ||
    modelRef.startsWith("bedrock-anthropic/") ||
    modelRef.startsWith("bedrock-openai/");
  const leaf = stripToLeafModel(modelRef);
  const { family, label } = detectFamilyAndLabel(leaf);
  const provider = resolveProvider(modelRef);
  return { family, isBedrock, label, provider };
}

function detectClaudeFamilyAndLabel(
  lower: string,
): Pick<ResolvedModel, "family" | "label"> | null {
  // Claude — claude-fable-5 / claude-fable-5-1 / claude-opus-4-7 /
  // claude-sonnet-5 / claude-haiku-4-5. Minor version is optional, matching the
  // opus/sonnet/haiku branch below.
  const claudeFableMatch = /^claude-fable-(\d+)(?:-(\d+))?/.exec(lower);
  if (claudeFableMatch) {
    const [, major, minor] = claudeFableMatch;
    return {
      family: "fable",
      label: minor ? `FABLE ${major}.${minor}` : `FABLE ${major}`,
    };
  }

  // Minor version is optional — single-digit ids (claude-sonnet-5) label as "SONNET 5".
  const claudeMatch = /^claude-(opus|sonnet|haiku)-(\d+)(?:-(\d+))?/.exec(
    lower,
  );
  if (claudeMatch) {
    const [, tier, major, minor] = claudeMatch;
    const family = familyForClaudeTier(tier);
    return {
      family,
      label: minor
        ? `${family.toUpperCase()} ${major}.${minor}`
        : `${family.toUpperCase()} ${major}`,
    };
  }

  return null;
}

function detectGptFamilyAndLabel(
  lower: string,
): Pick<ResolvedModel, "family" | "label"> | null {
  // GPT codex variants — gpt-5.4-codex, gpt-5.1-codex-mini, gpt-5.1-codex-max
  const gptCodexMatch = /^gpt-(\d+(?:\.\d+)?)-codex(?:-(mini|max))?/.exec(
    lower,
  );
  if (gptCodexMatch) {
    const [, ver, sub] = gptCodexMatch;
    return {
      family: "gpt-codex",
      label: sub ? `GPT ${ver} CODEX ${sub.toUpperCase()}` : `GPT ${ver} CODEX`,
    };
  }

  // GPT — gpt-6-astra, gpt-6-sol, gpt-5.6-sol, gpt-5.4, gpt-5.4-pro, gpt-5-mini
  const gptMatch = /^gpt-(\d+(?:\.\d+)?)(?:-(astra|mini|pro|sol))?/.exec(lower);
  if (gptMatch) {
    const [, ver, sub] = gptMatch;
    return {
      family: "gpt",
      label: sub ? `GPT ${ver} ${sub.toUpperCase()}` : `GPT ${ver}`,
    };
  }

  return null;
}
